// GanttConnections.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
  '#3f51b5', // Indygo
  '#f4a261', // Pomarańczowy
  '#e63946', // Czerwony
  '#2a9d8f', // Turkusowy / Morski
  '#9c27b0', // Fioletowy
  '#e91e63', // Różowy
  '#00bcd4', // Cyjan
  '#ff9800', // Głęboki pomarańcz
  '#8bc34a', // Jasnozielony
  '#795548', // Brązowy
];

const KOLOR_DOMYSLNY = '#888888';

// Funkcja pomocnicza sprawdzająca, czy dwa etapy mają wspólny numer arkusza
// Obsługuje np. "1,2,3" vs "3,4" -> zwraca true (wspólny 3)
const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (!arkuszeA || !arkuszeB) return false;

  // Rozbijamy ciągi tekstowe po przecinkach i usuwamy spacje
  const listA = String(arkuszeA).split(',').map(s => s.trim()).filter(Boolean);
  const listB = String(arkuszeB).split(',').map(s => s.trim()).filter(Boolean);

  // Sprawdzamy czy istnieje chociaż jeden wspólny element
  return listA.some(arkusz => listB.includes(arkusz));
};

const GanttConnections = ({
  groupedStages,
  processorIds,
  rowYPositions,
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showAllConections,
  showElementConections,
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    if (!showAllConections && !showElementConections && !selectedZamowienieId) {
      return [];
    }

    if (!minDate || !maxDate || !totalChartWidth) return [];

    const stagesByGroupAndIndex = {};
    const elementColorMap = {};

    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const y = rowYPositions[processorId] || 0;

      rowStages.forEach((stage) => {
        if (!stage.zamowienie_id) return;

        // Filtrowanie trybów
        if (showElementConections) {
          if (stage.zamowienie_id !== selectedZamowienieId) return;
          if (!stage.element_id) return;
        } else if (!showAllConections && stage.zamowienie_id !== selectedZamowienieId) {
          return;
        }

        // Przypisanie kolorów dla elementów
        if (showElementConections && stage.element_id && !(stage.element_id in elementColorMap)) {
          const currentIndex = Object.keys(elementColorMap).length;
          elementColorMap[stage.element_id] = currentIndex < PALETA_KOLOROW.length
            ? PALETA_KOLOROW[currentIndex]
            : KOLOR_DOMYSLNY;
        }

        const groupKey = showElementConections
          ? `elem_${stage.zamowienie_id}_${stage.element_id}`
          : `zam_${stage.zamowienie_id}`;

        const indeks = Number(stage.indeks_procesu) || 0;
        const { leftPx, widthPx } = calculateBarProperties(stage.start, stage.end);

        if (!stagesByGroupAndIndex[groupKey]) {
          stagesByGroupAndIndex[groupKey] = {};
        }

        if (!stagesByGroupAndIndex[groupKey][indeks]) {
          stagesByGroupAndIndex[groupKey][indeks] = [];
        }

        stagesByGroupAndIndex[groupKey][indeks].push({
          stageId: stage.global_id || stage.id,
          zamowienieId: stage.zamowienie_id,
          elementId: stage.element_id,
          arkuszeId: stage.arkusze_id, // Przypisujemy tekst z listą arkuszy "1,2,3"
          indeksProcesu: indeks,
          outPoint: { x: leftPx + widthPx, y },
          inPoint: { x: leftPx, y }
        });
      });
    });

    const lines = [];

    Object.keys(stagesByGroupAndIndex).forEach((groupKey) => {
      const indexesMap = stagesByGroupAndIndex[groupKey];
      
      const sortedIndexes = Object.keys(indexesMap)
        .map(Number)
        .sort((a, b) => a - b);

      for (let i = 0; i < sortedIndexes.length - 1; i++) {
        const currentIndeks = sortedIndexes[i];
        const nextIndeks = sortedIndexes[i + 1];

        const currentStages = indexesMap[currentIndeks];
        const nextStages = indexesMap[nextIndeks];

        currentStages.forEach((fromStage) => {
          nextStages.forEach((toStage) => {
            
            // --- WARUNEK WSPÓLNEGO ARKUSZA ---
            // Łączymy tylko te etapy z sąsiednich kroków, które mają wspólny numer arkusza
            if (!czyMajaWspolnyArkusz(fromStage.arkuszeId, toStage.arkuszeId)) {
              return; // brak wspólnego arkusza -> pomijamy połączenie
            }

            const strokeColor = showElementConections
              ? (elementColorMap[fromStage.elementId] || KOLOR_DOMYSLNY)
              : '#4d9aff';

            lines.push({
              id: `${groupKey}_${fromStage.stageId}_to_${toStage.stageId}`,
              zamowienieId: fromStage.zamowienieId,
              elementId: fromStage.elementId,
              isSelected: Number(fromStage.zamowienieId) === Number(selectedZamowienieId),
              from: fromStage.outPoint,
              to: toStage.inPoint,
              strokeColor,
              type: showElementConections ? 'element' : 'order'
            });
          });
        });
      }
    });

    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showAllConections,
    showElementConections,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(connectionLines.map(line => [line.elementId, line.strokeColor])).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    >
      <defs>
        <marker id="arrowhead-default" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#4d9aff" />
        </marker>
        <marker id="arrowhead-selected" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#ff4d4f" />
        </marker>

        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-${elementId}`}
            id={`arrowhead-elem-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, isSelected, id, type, strokeColor, elementId } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;

        let finalColor = strokeColor;
        let markerId = `url(#arrowhead-elem-${elementId})`;

        if (!showElementConections && isSelected) {
          finalColor = '#ff4d4f';
          markerId = 'url(#arrowhead-selected)';
        } else if (!showElementConections) {
          markerId = 'url(#arrowhead-default)';
        }

        return (
          <g key={`connection-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={finalColor}
              strokeWidth="1"
              strokeDasharray={type === 'element' ? 'none' : '5 3'}
              markerEnd={markerId}
            />
            <circle cx={from.x} cy={from.y} r="3.5" fill={finalColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttConnections);